import React, { useEffect, useState } from 'react';
import {
  IInventorySum,
  IPendingInventory,
} from '../entities/inventory.entities';
import { SelectAccountSetting } from '../store/extra/extra.selector';
import { translate } from '../lib/lang.helper';
import { IAssessmentResource, IFarmResource } from '../entities/farms.entities';
import { useSelector } from 'react-redux';
import { selectLang } from '../store/ui/ui.selector';
import { sendSingleRequest } from '../apis';
import {
  defaultDateFormat,
  displayValueUnit,
  getUnitOfSpecie,
} from '../lib/common.helpers';
import { formatNumber } from '../entities/util-functions';
import { Spin } from 'antd';
import { selectAccount } from '../store/auth/auth.selector';
import { IAccountSetting } from '../entities/user.entities';
import { selectFarmsData } from '../store/farms/farms.selector';
import ModalComponent from './shared/modal/Modal';

export const LineInventoriesLabel = (inventories: IPendingInventory[]) =>
  inventories.map(x => (
    <span key={`ivt-${x.id}`}>
      <span>
        {x.name} - {x.pending_quantity}
      </span>
      <br />
    </span>
  ));

export const SeedInventoriesLabel = (ivt_seeds: IInventorySum[]) =>
  ivt_seeds.map(x => (
    <span key={`seed-${x.inventory_id}`}>
      <span>
        {x.inventory_name} - {x.quantity}
      </span>
      <br />
    </span>
  ));

export const getStageLabel = (
  labels: IAccountSetting['stage_labels'],
  stage: number,
) =>
  !labels || labels.length < stage
    ? stage.toString()
    : (labels[stage - 1].label ?? stage.toString())
      ? (labels[stage - 1].label ?? stage.toString())
      : stage.toString();

export const StageLabelSpan: React.FC<{
  stage: number;
  farmId?: number | undefined;
}> = ({ stage, farmId }) => {
  const labels = SelectAccountSetting(farmId)?.stage_labels;

  return <span>{getStageLabel(labels, stage ?? 1)}</span>;
};

export const AreaClosedConfirmModal: React.FC<{
  farmId?: number;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ farmId, onConfirm, onCancel }) => {
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);

  useEffect(() => {
    const farm = farmsData.find(f => f.id === farmId);
    if (!farm || !farm.is_closed) {
      onConfirm();
    }
  }, [farmsData, farmId, onConfirm]);

  return (
    <ModalComponent
      visible={true}
      onCancel={onCancel}
      onConfirm={onConfirm}
      type='warning'
      title={translate(lang, 'Warning')}
      text={
        <div className='header-alert'>
          {translate(lang, '_area_closed_submit_confirm')}
        </div>
      }
    />
  );
};

interface ILineProp {
  farm_id: number;
  line_id: number;
  predicted_harvest_date: number;
  predicted_harvest_amount: number;
}

export const ForecastCell: React.FC<{
  className?: string;
  farm: IFarmResource;
  lineProps?: ILineProp[] | undefined;
}> = ({ farm, className, lineProps }) => {
  const lang = useSelector(selectLang);

  const [forecast, setForecast] = useState<{
    line_name: string;
    harvest_date: string | null;
    harvest_amount: string;
  } | null>();

  useEffect(() => {
    (async () => {
      try {
        if (lineProps === undefined) {
          const res = await sendSingleRequest(
            { farm_id: farm.id },
            'GET',
            'api/farm/lines-props',
            true,
          );
          if (res.status) {
            const lines: ILineProp[] = Array.isArray(res.data)
              ? res.data.sort(
                  (a: ILineProp, b: ILineProp) =>
                    a.predicted_harvest_date - b.predicted_harvest_date,
                )
              : [];
            if (lines.length <= 0) {
              setForecast(null);
            } else {
              const line = farm.lines?.find(l => l.id === lines[0].line_id);
              if (line) {
                setForecast({
                  line_name: line.line_name,
                  harvest_date: defaultDateFormat(
                    lines[0].predicted_harvest_date,
                  ),
                  harvest_amount: formatNumber(
                    lines[0].predicted_harvest_amount / 1000,
                  ),
                });
              } else {
                setForecast(null);
              }
            }
          } else {
            setForecast(null);
          }
        } else {
          const lines: ILineProp[] = lineProps
            .filter(x => x.farm_id === farm.id)
            .sort(
              (a, b) => a.predicted_harvest_date - b.predicted_harvest_date,
            );
          if (lines.length <= 0) {
            setForecast(null);
          } else {
            const line = farm.lines?.find(l => l.id === lines[0].line_id);
            if (line) {
              setForecast({
                line_name: line.line_name,
                harvest_date: defaultDateFormat(
                  lines[0].predicted_harvest_date,
                ),
                harvest_amount: formatNumber(
                  lines[0].predicted_harvest_amount / 1000,
                ),
              });
            }
          }
        }
      } catch (e) {
        console.error('ForecastCell error:', e);
        setForecast(null);
      }
    })();
  }, [lineProps, farm]);

  return forecast === undefined ? (
    <div style={{ width: '100%', textAlign: 'center' }}>
      <Spin size='small' />
    </div>
  ) : forecast === null ? (
    <span>{'-'}</span>
  ) : (
    <div className={className}>
      <div>
        {`${translate(lang, 'Forecasted line')}: ${forecast.line_name}`}
      </div>
      <div>
        {`${translate(lang, 'Estimated date')}: ${forecast.harvest_date}`}
      </div>
      <div>
        {`${translate(lang, 'Estimated tonnes')}: ${forecast.harvest_amount}`}
      </div>
    </div>
  );
};

export const SeedSizeRange: React.FC<{
  data:
    | {
        spat_size: number | null | undefined;
        spat_size_max: number | null | undefined;
      }
    | undefined;
}> = ({ data }) => {
  const unitType = useSelector(selectAccount)?.display_unit;

  if (!data || data.spat_size === null || data.spat_size === undefined) {
    return <>{'-'}</>;
  }
  if (data.spat_size_max === null || data.spat_size_max === undefined) {
    return <>{displayValueUnit(data.spat_size, unitType, 'size_mm', '-')}</>;
  }

  const unit = getUnitOfSpecie(unitType, 'size_mm');
  const min = Math.round(data.spat_size * 1000) / 1000;
  const max = Math.round(data.spat_size_max * 1000) / 1000;

  return <>{`${min}-${max} ${unit}`}</>;
};

export const HarvestSizeRange: React.FC<{
  data:
    | {
        shell_length: number | null | undefined;
        shell_length_max: number | null | undefined;
      }
    | undefined;
}> = ({ data }) => {
  const unitType = useSelector(selectAccount)?.display_unit;

  if (!data || data.shell_length === null || data.shell_length === undefined) {
    return <>{'-'}</>;
  }
  if (data.shell_length_max === null || data.shell_length_max === undefined) {
    return <>{displayValueUnit(data.shell_length, unitType, 'size_mm', '-')}</>;
  }
  const unit = getUnitOfSpecie(unitType, 'size_mm');
  const min = Math.round(data.shell_length * 1000) / 1000;
  const max = Math.round(data.shell_length_max * 1000) / 1000;

  return <>{`${min}-${max} ${unit}`}</>;
};

export const SeedDensityRange = (
  data:
    | {
        density?: number | null;
        density_max?: number | null;
        density_unit?: string | null;
      }
    | undefined,
) => {
  const unitType = useSelector(selectAccount)?.display_unit;

  if (!data) {
    return '-';
  }
  if (data.density === null || data.density === undefined) {
    return '-';
  }

  const unitSpecie =
    data.density_unit === 'kgs per m' ? 'kg_per_m' : 'spat_mer_m';

  if (data.density_max === null || data.density_max === undefined) {
    return displayValueUnit(data.density, unitType, unitSpecie as any, '-');
  }

  const unit = getUnitOfSpecie(unitType, unitSpecie as any);
  const min = Math.round(data.density * 1000) / 1000;
  const max = Math.round(data.density_max * 1000) / 1000;

  return `${min}-${max} ${unit}`;
};

export const AssessmentSizeRange: React.FC<{ data?: IAssessmentResource }> = ({
  data,
}) => {
  const unitType = useSelector(selectAccount)?.display_unit;

  if (!data) {
    return <>{'-'}</>;
  }
  const min = Math.round(data.shell_size.min * 1000) / 1000;
  const max = Math.round(data.shell_size.max * 1000) / 1000;
  const avg = Math.round(data.shell_size.avg * 1000) / 1000;
  const unit = getUnitOfSpecie(unitType, 'size_mm');

  return <>{`${min} ~ ${max}, ${avg} ${unit}`}</>;
};
